/**
 * Keeps a half-finished check-in alive: the answer in progress while the patient
 * moves around the loop, and the loop's own place in it while they are off looking
 * at something else in the app.
 *
 * The loop renders one element at a time, so stepping back unmounts the question
 * you were on. Anything it was holding — the text in the field, which chip was
 * tapped, the medication rows added so far, the interview turns already spent on
 * an LLM call — died with it, and coming forward again showed a blank screen.
 *
 * The same thing happened one level up, and worse: following the link to the
 * MasterChart unmounted the whole page, so coming back restarted the check-in
 * from the opening screen. This provider is therefore mounted ABOVE <Routes> in
 * PatientRoutes, exactly where ChartMindSessionProvider sits and for exactly the
 * same reason, and the shell mirrors its progress into it (see
 * VisitStartSessionShell).
 *
 * react-hook-form does NOT survive that on its own, and it is worth being precise
 * about why, because it looks like it should. `shouldUnregister: false` (the v7
 * default) keeps a value when the FIELD unmounts, not when the component holding
 * `useForm` unmounts — the form state lives in that component. The library's own
 * answer for a wizard is to hoist one `useForm` above the steps and share it
 * through `FormProvider`. That is wrong here: the elements have per-screen
 * validation rules, and one shared form would validate a field belonging to a
 * question the patient is not looking at. So the values live outside the form
 * instead, and each element seeds from them.
 *
 * A ref, not state. Drafts change on every keystroke and nothing renders from
 * them, so putting them in state would re-render the whole loop per character.
 *
 * NOTHING HEAVY IN HERE. PatientRoutes imports the provider eagerly, above
 * <Routes>, so anything this module pulls in lands in the patient dashboard chunk
 * and undoes VisitStart's lazy split. `useVisitStartDraftForm` therefore lives in
 * visitStartDraftForm.js, next to react-hook-form, and is imported only by the
 * elements — which are already behind the lazy boundary.
 *
 * In memory only, and deliberately: a draft is not an answer. Answers go to
 * Firestore when the element completes, and a half-typed one has no business
 * being stored on a patient record. Reloading the page loses drafts, which is the
 * same thing every other form in the app does.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

export const VisitStartDraftContext = createContext(null);

export function VisitStartDraftProvider({ children }) {
  const drafts = useRef(new Map());

  const value = useMemo(
    () => ({
      read: (key) => (key ? drafts.current.get(key) : undefined),
      write: (key, draft) => {
        if (key) drafts.current.set(key, draft);
      },
      clear: (key) => {
        if (key) drafts.current.delete(key);
      },
      /** Everything: a finished check-in must not come back as an in-flight one. */
      clearAll: () => drafts.current.clear(),
    }),
    [],
  );

  return (
    <VisitStartDraftContext.Provider value={value}>
      {children}
    </VisitStartDraftContext.Provider>
  );
}

VisitStartDraftProvider.propTypes = { children: PropTypes.node };

/**
 * The raw store, for the rare case that needs to drop a draft by hand — a field
 * that was submitted, so its text should not come back.
 * @returns {?{read: Function, write: Function, clear: Function, clearAll: Function}}
 */
export function useVisitStartDrafts() {
  return useContext(VisitStartDraftContext);
}

/**
 * `useState`, seeded from and mirrored into the draft store.
 *
 * This is what restores the SHAPE of a screen and not just its text: which chip
 * was tapped, which phase the element was in, the rows already added. Without it
 * a patient who steps back and forward lands on the opening question again with
 * their work discarded.
 *
 * @param {?string} draftKey Stable per question and per value, e.g. "medication-list:items".
 * @param {*} initialValue Used when the store has nothing yet. A function is called lazily, as with useState.
 */
export function useVisitStartDraftState(draftKey, initialValue) {
  const store = useContext(VisitStartDraftContext);

  const [value, setValue] = useState(() => {
    const stored = draftKey && store ? store.read(draftKey) : undefined;
    if (stored !== undefined) return stored;
    return typeof initialValue === "function" ? initialValue() : initialValue;
  });

  useEffect(() => {
    if (draftKey && store) store.write(draftKey, value);
  }, [draftKey, store, value]);

  return [value, setValue];
}
